import { useTranslation } from "react-i18next"

import { BadgeMedallionStrip } from "@/components/badges/badge-medallion-strip"
import {
  type MedallionViewModel,
  medallionName,
  medallionPopover,
  toMedallionViewModels,
} from "@/lib/badge-medallions"
import type { BadgeMedallionRow } from "@/types/badges"

/**
 * OKS-970 — the medallion strip inside one `<CrewRosterCell>`.
 *
 * The roster's analogue of `<CreatorBadgesSection>`: this component exists so
 * that every `t()` call and the zero-badge decision live OUTSIDE
 * `<BadgeMedallionStrip>`, which composes no copy of its own. `<CrewRosterCell>`
 * stays a layout component rather than growing a twenty-line label object.
 *
 * # Nothing at all when the crewmate holds no badges (BRIEF AC2 / D5)
 *
 * The strip renders a muted "Aucun badge obtenu pour le moment." for an empty
 * list, which is right on the brand's Card and wrong under a peer's face in a
 * 64px cell — thirty of them read as a public deficiency notice. So the guard
 * is HERE, and it is an active choice at the call site rather than an inherited
 * default: a zero-badge cell must be byte-identical to the pre-OKS-970 one.
 *
 * # Inert (BRIEF D4)
 *
 * `interactive={false}` — a 28px popover trigger is far under the 44px touch
 * minimum the cell's own doc-comment observes, thirty cells would add up to
 * sixty tab stops to a social-proof grid, and the cell is deliberately
 * non-navigating in v1. The medallion is then a `role="img"` with a complete,
 * count-bearing accessible name, so assistive tech gets more than the glyph.
 * `expandable={false}` follows: the `+N` is inert text, not a control.
 */
export interface CrewRosterMedallionsProps {
  /** Server-ordered rows for ONE crewmate. */
  badges: BadgeMedallionRow[]
  /** Names the list per crewmate, so 30 cells do not announce one label. */
  firstName: string
}

/**
 * BRIEF D3 — a width fact, not a product judgement: two 28px discs at the
 * strip's `corner` `gap-1.5` measure 62px and fit the `w-16` cell on one line.
 * A third wraps to a second disc row inside the cell.
 */
const MAX_VISIBLE = 2

export function CrewRosterMedallions({
  badges,
  firstName,
}: CrewRosterMedallionsProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || "fr"

  if (badges.length === 0) return null

  // Maps, never sorts — the prestige order is the server's (BRIEF AC3).
  const items = toMedallionViewModels(badges)

  const nameOf = (vm: MedallionViewModel) => medallionName(vm, t)

  return (
    <BadgeMedallionStrip
      items={items}
      size="corner"
      max={MAX_VISIBLE}
      expandable={false}
      interactive={false}
      // `cn` is twMerge: `gap-1` overrides the component's `gap-2`, and
      // `items-center` shrink-wraps the inner <ul> so a single disc centres
      // under the avatar instead of sitting flush left in the 64px column
      // (the inert `+N` span is `w-fit` and would left-align with it).
      className="items-center gap-1"
      labels={{
        strip: t("crew_hub.roster.badges_aria", { name: firstName }),
        // Unreachable behind the guard above; supplied because the prop is
        // required and this is the correct string if it ever renders.
        empty: t("badges.medallions.empty"),
        // A repeated family MUST speak its count: the `×N` chip is
        // aria-hidden, so the count reaches assistive tech only here.
        medallion: (vm) =>
          vm.count > 1
            ? t("badges.aria.tile_earned_repeated", {
                name: nameOf(vm),
                tier: t(`badges.tier.${vm.tier}`),
                count: vm.count,
              })
            : t("badges.aria.tile_earned_brief", {
                name: nameOf(vm),
                tier: t(`badges.tier.${vm.tier}`),
              }),
        countChip: (count) => t("badges.medallions.count_chip", { count }),
        // OKS-975 — the roster's answer to "what is this disc?" (AC9). The
        // strip stays inert, so a sighted mouse user gets the family name from
        // a native tooltip rather than a popover: no tab stop, no touch-target
        // change, no chrome, so cell height is untouched. Deliberately NOT
        // paired with a `hint` line — there is nothing to select here.
        hoverName: (vm) => nameOf(vm),
        // Never called at `interactive={false}` — passed as the REAL adapter
        // rather than a stub, so OKS-975's affordance decision stays a
        // one-word change here.
        //
        // OKS-975 — and now that promise is finally true of the OUTPUT and not
        // just of the prop. This used to be an inline copy of the rule, kept
        // identical to `<CreatorBadgesSection>`'s copy by hand after OKS-970's
        // F2 found them diverged. Being unreachable, nothing could observe the
        // next divergence. One shared function means there is no second copy
        // to drift.
        popover: (vm) => medallionPopover(vm, t, locale),
        more: (hidden) => t("badges.medallions.show_more", { count: hidden }),
        // Never called at `expandable={false}`; same reasoning as `popover`.
        moreAria: (hidden) =>
          t("badges.medallions.show_more_aria", { count: hidden }),
        less: t("badges.medallions.show_less"),
      }}
    />
  )
}
